import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, Filter, ArrowRight, Clock, CheckCircle, 
  User, DollarSign, AlertCircle, UserPlus, Users
} from 'lucide-react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api, useAuth } from '../../lib/auth';
import { formatDate, formatCurrency, getStatusColor, getStatusLabel, cn, ORDER_STATUSES } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

export default function AdminOrders() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [orders, setOrders] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [proposalDialog, setProposalDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [proposalData, setProposalData] = useState({
    total: '',
    covered_amount: '',
    description: ''
  });

  useEffect(() => {
    loadOrders();
    loadProposals();
    loadTeamMembers(); // Load for all users to display assignee names
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOrders = async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const response = await api.getOrders(params);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProposals = async () => {
    try {
      const response = await api.getProposals();
      setProposals(response.data);
    } catch (error) {
      console.error('Failed to load proposals:', error);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await api.getUsers();
      // Filter to team members and pod team members
      const members = response.data.filter(u => 
        ['team_member', 'pod_team_member', 'admin'].includes(u.role)
      );
      setTeamMembers(members);
    } catch (error) {
      console.error('Failed to load team members:', error);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await api.updateOrderStatus(orderId, { status: newStatus });
      toast.success('Order status updated');
      loadOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssignOrder = async () => {
    if (!selectedOrder || !selectedAssignee) {
      toast.error('Please select a team member');
      return;
    }

    try {
      await api.assignOrder(selectedOrder.id, selectedAssignee);
      toast.success('Order assigned successfully');
      setAssignDialog(false);
      setSelectedOrder(null);
      setSelectedAssignee('');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign order');
    }
  };

  const getAssigneeName = (assignedTo) => {
    if (!assignedTo) return null;
    const member = teamMembers.find(m => m.id === assignedTo);
    return member?.name || 'Unknown';
  };

  const handleCreateProposal = async () => {
    if (!selectedOrder || !proposalData.total) {
      toast.error('Please enter a total amount');
      return;
    }

    try {
      await api.createProposal({
        order_id: selectedOrder.id,
        line_items_json: [{ description: proposalData.description || 'Service Fee', amount: parseFloat(proposalData.total) }],
        total: parseFloat(proposalData.total),
        covered_amount: parseFloat(proposalData.covered_amount) || 0
      });
      toast.success('Proposal created');
      setProposalDialog(false);
      setSelectedOrder(null);
      setProposalData({ total: '', covered_amount: '', description: '' });
      loadOrders();
      loadProposals();
    } catch (error) {
      toast.error('Failed to create proposal');
    }
  };

  const handleMarkProposalPaid = async (proposalId) => {
    try {
      await api.markProposalPaid(proposalId);
      toast.success('Proposal marked as paid');
      loadProposals();
      loadOrders();
    } catch (error) {
      toast.error('Failed to mark proposal as paid');
    }
  };

  const getOrderProposals = (orderId) => {
    return proposals.filter(p => p.order_id === orderId);
  };

  const filteredOrders = orders.filter(order => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      order.service_title?.toLowerCase().includes(searchLower) ||
      order.department_name?.toLowerCase().includes(searchLower) ||
      order.id.toLowerCase().includes(searchLower)
    );
  });

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl font-bold text-white mb-2">Manage Orders</h1>
          <p className="text-gray-400">View, update, and create proposals for orders</p>
        </motion.div>

        {/* Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-xl p-4 mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type="text"
                placeholder="Search orders..."
                className="pl-10 input-dark"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-orders-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 input-dark">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                <SelectItem value="all">All Statuses</SelectItem>
                {ORDER_STATUSES.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Orders List */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-16">
              <Clock className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No orders found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map((order, i) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="glass rounded-xl p-6"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={cn(
                          "px-3 py-1 rounded-full text-xs font-medium border",
                          getStatusColor(order.status)
                        )}>
                          {getStatusLabel(order.status)}
                        </span>
                        <span className="text-sm text-gray-500">
                          Ordered: {formatDate(order.created_at)}
                        </span>
                        {(order.structured_data_json?.desired_due_date || order.desired_due_date) && (
                          <span className="text-sm text-orange-400">
                            • Due: {formatDate(order.structured_data_json?.desired_due_date || order.desired_due_date)}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-white text-lg mb-1">
                        {order.structured_data_json?.task_name || order.service_title || order.user_name || 'Order'}
                        {order.structured_data_json?.client_name && (
                          <span className="text-gray-400 font-normal text-base ml-2">
                            — {order.structured_data_json.client_name}
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-400">
                        {order.department_name} • ID: {order.id.slice(0, 8)}...
                        {order.assigned_to && (
                          <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                            <Users className="w-3 h-3 inline mr-1" />
                            Assigned to: {getAssigneeName(order.assigned_to)}
                          </span>
                        )}
                      </p>
                      
                      {/* Proposals Display */}
                      {getOrderProposals(order.id).length > 0 && (
                        <div className="mt-3 space-y-2">
                          {getOrderProposals(order.id).map((proposal) => (
                            <div key={proposal.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                              <div className="flex items-center gap-3">
                                <span className={cn(
                                  "px-2 py-1 rounded text-xs font-medium",
                                  proposal.status === 'sent' ? "bg-yellow-500/20 text-yellow-400" :
                                  proposal.status === 'approved' ? "bg-blue-500/20 text-blue-400" :
                                  proposal.status === 'paid' ? "bg-green-500/20 text-green-400" :
                                  "bg-gray-500/20 text-gray-400"
                                )}>
                                  {proposal.status === 'sent' ? 'Pending' :
                                   proposal.status === 'approved' ? 'Approved' :
                                   proposal.status === 'paid' ? 'Paid' : proposal.status}
                                </span>
                                <span className="text-sm text-white font-medium">
                                  {formatCurrency(proposal.total)}
                                </span>
                                {proposal.covered_amount > 0 && (
                                  <span className="text-xs text-green-400">
                                    Co-Op: {formatCurrency(proposal.covered_amount)}
                                  </span>
                                )}
                                {proposal.due_amount > 0 && (
                                  <span className="text-xs text-orange-400">
                                    Due: {formatCurrency(proposal.due_amount)}
                                  </span>
                                )}
                              </div>
                              
                              {/* Mark as Paid Button */}
                              {isAdmin && proposal.status === 'approved' && (
                                <Button
                                  onClick={() => handleMarkProposalPaid(proposal.id)}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  data-testid={`mark-paid-${proposal.id}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Mark Paid
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      {/* Assign to Team Member (Admin only) */}
                      {isAdmin && (
                        <Button
                          onClick={() => {
                            setSelectedOrder(order);
                            setSelectedAssignee(order.assigned_to || '');
                            setAssignDialog(true);
                          }}
                          variant="outline"
                          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                          data-testid={`assign-order-${order.id}`}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          {order.assigned_to ? 'Reassign' : 'Assign'}
                        </Button>
                      )}

                      {/* Status Update */}
                      <Select 
                        value={order.status} 
                        onValueChange={(value) => handleStatusUpdate(order.id, value)}
                      >
                        <SelectTrigger className="w-40 input-dark" data-testid={`status-select-${order.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-white/10">
                          {ORDER_STATUSES.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Create Proposal Button (only for submitted/accepted orders without existing proposals) */}
                      {['submitted', 'accepted'].includes(order.status) && getOrderProposals(order.id).length === 0 && (
                        <Button
                          onClick={() => {
                            setSelectedOrder(order);
                            setProposalDialog(true);
                          }}
                          className="btn-secondary"
                          data-testid={`create-proposal-${order.id}`}
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Create Proposal
                        </Button>
                      )}

                      <Link to={`/orders/${order.id}`}>
                        <Button variant="ghost" className="text-gray-400 hover:text-white">
                          View <ArrowRight className="ml-1 w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Proposal Dialog */}
        <Dialog open={proposalDialog} onOpenChange={setProposalDialog}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Create Proposal</DialogTitle>
              <DialogDescription className="text-gray-400">
                Set pricing for order: {selectedOrder?.service_title}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Description</label>
                <Input
                  type="text"
                  value={proposalData.description}
                  onChange={(e) => setProposalData({ ...proposalData, description: e.target.value })}
                  placeholder="Service fee, additional work, etc."
                  className="input-dark"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Total Amount ($)</label>
                <Input
                  type="number"
                  value={proposalData.total}
                  onChange={(e) => setProposalData({ ...proposalData, total: e.target.value })}
                  placeholder="0.00"
                  className="input-dark"
                  data-testid="proposal-total-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Co-Op Coverage ($)</label>
                <Input
                  type="number"
                  value={proposalData.covered_amount}
                  onChange={(e) => setProposalData({ ...proposalData, covered_amount: e.target.value })}
                  placeholder="0.00"
                  className="input-dark"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Amount covered by co-op funds (will be deducted from total)
                </p>
              </div>

              <div className="pt-4 flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setProposalDialog(false)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateProposal}
                  className="flex-1 btn-primary"
                  data-testid="submit-proposal-btn"
                >
                  Create Proposal
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Assign Order Dialog */}
        <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Assign Order</DialogTitle>
              <DialogDescription className="text-gray-400">
                Assign &ldquo;{selectedOrder?.service_title}&rdquo; to a team member
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Select Team Member</label>
                <Select value={selectedAssignee || 'unassigned'} onValueChange={(val) => setSelectedAssignee(val === 'unassigned' ? '' : val)}>
                  <SelectTrigger className="input-dark" data-testid="assign-team-member-select">
                    <SelectValue placeholder="Choose a team member..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10 max-h-60">
                    <SelectItem value="unassigned">
                      <span className="text-gray-400">Unassigned</span>
                    </SelectItem>
                    {teamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "w-2 h-2 rounded-full",
                            member.role === 'admin' ? "bg-accent" :
                            member.role === 'pod_team_member' ? "bg-blue-400" :
                            "bg-gray-400"
                          )} />
                          <span>{member.name}</span>
                          <span className="text-xs text-gray-500">
                            ({member.role === 'pod_team_member' ? 'POD Team' : member.role})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedOrder?.assigned_to && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-400">
                    <Users className="w-4 h-4 inline mr-2" />
                    Currently assigned to: <strong>{getAssigneeName(selectedOrder.assigned_to)}</strong>
                  </p>
                </div>
              )}

              <div className="pt-4 flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setAssignDialog(false);
                    setSelectedAssignee('');
                  }}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAssignOrder}
                  className="flex-1 btn-primary"
                  data-testid="confirm-assign-btn"
                >
                  {selectedAssignee ? 'Assign Order' : 'Remove Assignment'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
